import type { TrackerAdapter, PvOptions } from './types';
import { PV_EVENT_NAME, MAX_BATCH_BUFFER_SIZE } from './constants';
import { isBrowser } from './utils';

/** 批量上报包装器。在底层 adapter 外套一层缓冲，降低高频上报的带宽占用。 */
export class BatchedAdapter implements TrackerAdapter {
  private adapter: TrackerAdapter;
  private buffer: Array<{ eventName: string; properties?: Record<string, any> }> = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private batchInterval: number;
  private _handleVisibilityChange: (() => void) | null = null;
  private _handlePageHide: (() => void) | null = null;

  constructor(adapter: TrackerAdapter, options: { batchInterval: number }) {
    this.adapter = adapter;
    this.batchInterval = options.batchInterval;
  }

  // ─── 透传方法 ──────────────────────────────────────────

  init(appId: number, options: Record<string, any>): void {
    this.adapter.init(appId, options);
    this._startTimer();
  }

  start(): void {
    this.adapter.start();
  }

  getDefaultProps(): Record<string, any> {
    return this.adapter.getDefaultProps();
  }

  // ─── 缓冲方法 ──────────────────────────────────────────

  event(eventName: string, properties?: Record<string, any>): void {
    this.buffer.push({ eventName, properties });
    this._checkBufferSize();
  }

  pageView(options?: PvOptions): void {
    // 将 PV 转为 event 存储，flush 时统一通过 adapter.event 发送
    this.buffer.push({ eventName: PV_EVENT_NAME, properties: options });
    this._checkBufferSize();
  }

  // ─── 先 flush 再透传 ────────────────────────────────────

  login(userId: string): void {
    this.flush();
    this.adapter.login(userId);
  }

  logout(): void {
    this.flush();
    this.adapter.logout();
  }

  // ─── 生命周期 ──────────────────────────────────────────

  /** 立即 flush 所有缓冲事件，并清理定时器与事件监听 */
  destroy(): void {
    this.flush();
    this._stopTimer();
  }

  /** 立即发送所有缓冲事件 */
  flush(): void {
    if (this.buffer.length === 0) return;

    const batch = this.buffer.splice(0);
    for (const item of batch) {
      try {
        this.adapter.event(item.eventName, item.properties);
      } catch (error) {
        console.warn('[yl-nsm-track] Failed to flush batched event', error);
      }
    }
  }

  // ─── 私有方法 ──────────────────────────────────────────

  /** 启动定时 flush 与页面可见性监听 */
  private _startTimer(): void {
    if (!isBrowser()) return;

    // 页面隐藏时立即 flush，防止用户离开时数据丢失
    this._handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        this.flush();
      }
    };
    document.addEventListener('visibilitychange', this._handleVisibilityChange);

    // 页面卸载时兜底 flush（visibilitychange 的 handler 可能先于 stay 模块执行，
    // 导致 page_stay 等事件进入缓冲区后未被及时发送。pagehide 在 visibilitychange
    // 之后触发，确保缓冲区中的所有事件都能被发送）
    this._handlePageHide = () => {
      this.flush();
    };
    window.addEventListener('pagehide', this._handlePageHide);

    // 定时 flush
    this.timer = setInterval(() => this.flush(), this.batchInterval);
  }

  /** 停止定时器与事件监听 */
  private _stopTimer(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this._handleVisibilityChange !== null) {
      document.removeEventListener('visibilitychange', this._handleVisibilityChange);
      this._handleVisibilityChange = null;
    }
    if (this._handlePageHide !== null) {
      window.removeEventListener('pagehide', this._handlePageHide);
      this._handlePageHide = null;
    }
  }

  /** 检查缓冲区大小，超过上限立即 flush */
  private _checkBufferSize(): void {
    if (this.buffer.length >= MAX_BATCH_BUFFER_SIZE) {
      this.flush();
    }
  }
}